/*
 * Copyright (c) 2022-2024 AccelByte Inc. All Rights Reserved
 * This is licensed software from AccelByte Inc, for limitations
 * and restrictions contact your company contract manager.
 */
/**
 * AUTO GENERATED
 */
import { CodeGenUtil, IResponse, IResponseWithSync, SDKRequestConfig, SdkCache, Validate } from '@accelbyte/sdk'
import { AxiosInstance } from 'axios'
import { z } from 'zod'
import { JwkSet } from '../../generated-definitions/JwkSet.js'
import { TokenResponse } from '../../generated-definitions/TokenResponse.js'
import { TokenVerifyResponse } from '../../generated-definitions/TokenVerifyResponse.js'

export class OAuth20$ {
  // @ts-ignore
  constructor(
    private axiosInstance: AxiosInstance,
    private namespace: string,
    private cache = false,
    private isValidationEnabled = true
  ) {}

  /**
   * <p>This endpoint serves public keys for verifying JWT access tokens generated by this service.</p> <p>When a client application wants to verify a JWT token, it needs to get the 'kid' value found in the JWT token header and use it to look up the corresponding public key from a set returned by this endpoint. The client application can then use that public key to verify the JWT.</p> <p>A client application might cache the keys so it doesn't need to do request every time it needs to verify a JWT token. If a client application caches the keys and a key with the same 'kid' cannot be found in the cache, it should then try to refresh the keys by making a request to this endpoint again.</p> <p>Please refer to the RFC for more information about JWK (JSON Web Key): https://tools.ietf.org/html/rfc7517</p>
   */
  getOauthJwks(): Promise<IResponseWithSync<JwkSet>> {
    const params = {} as SDKRequestConfig
    const url = '/ic/v1/oauth/jwks'
    const resultPromise = this.axiosInstance.get(url, { params })

    const res = () =>
      this.isValidationEnabled ? Validate.responseType(() => resultPromise, JwkSet, 'JwkSet') : Validate.unsafeResponse(() => resultPromise)

    if (!this.cache) {
      return SdkCache.withoutCache(res)
    }
    const cacheKey = url + CodeGenUtil.hashCode(JSON.stringify({ params }))
    return SdkCache.withCache(cacheKey, res)
  }

  /**
   * OAuth2 access token generation
   */
  postOauthToken(data: {
    grant_type: 'authorization_code' | 'client_credentials' | 'password' | 'refresh_token'
    username?: string | null
    password?: string | null
    code?: string | null
    code_verifier?: string | null
    client_id?: string | null
    redirect_uri?: string | null
    refresh_token?: string | null
    extend_exp?: boolean | null
  }): Promise<IResponse<TokenResponse>> {
    const params = {} as SDKRequestConfig
    const url = '/ic/v1/oauth/token'
    const resultPromise = this.axiosInstance.post(url, CodeGenUtil.getFormUrlEncodedData(data), {
      ...params,
      headers: { ...params.headers, 'content-type': 'application/x-www-form-urlencoded' }
    })

    return this.isValidationEnabled
      ? Validate.responseType(() => resultPromise, TokenResponse, 'TokenResponse')
      : Validate.unsafeResponse(() => resultPromise)
  }

  /**
   * This endpoint is used to remove **access_token**, **refresh_token** from cookie.
   */
  createOauthLogout(): Promise<IResponse<unknown>> {
    const params = {} as SDKRequestConfig
    const url = '/ic/v1/oauth/logout'
    const resultPromise = this.axiosInstance.post(url, null, { params })

    return this.isValidationEnabled
      ? Validate.responseType(() => resultPromise, z.unknown(), 'z.unknown()')
      : Validate.unsafeResponse(() => resultPromise)
  }

  /**
   * This endpoint requires all requests to have Authorization header set with Basic access authentication constructed from client id and client secret.
   */
  postOauthVerify(data: { token: string | null }): Promise<IResponse<TokenVerifyResponse>> {
    const params = {} as SDKRequestConfig
    const url = '/ic/v1/oauth/verify'
    const resultPromise = this.axiosInstance.post(url, CodeGenUtil.getFormUrlEncodedData(data), {
      ...params,
      headers: { ...params.headers, 'content-type': 'application/x-www-form-urlencoded' }
    })

    return this.isValidationEnabled
      ? Validate.responseType(() => resultPromise, TokenVerifyResponse, 'TokenVerifyResponse')
      : Validate.unsafeResponse(() => resultPromise)
  }

  /**
   * OAuth2 authorize request
   */
  getOauthAuthorize(queryParams: {
    response_type: 'code'
    client_id: string | null
    redirect_uri?: string | null
    state?: string | null
    code_challenge?: string | null
    code_challenge_method?: 'S256' | 'plain'
    target_auth_page?: string | null
  }): Promise<IResponseWithSync<unknown>> {
    const params = { code_challenge_method: 'plain', ...queryParams } as SDKRequestConfig
    const url = '/ic/v1/oauth/authorize'
    const resultPromise = this.axiosInstance.get(url, { params })

    const res = () =>
      this.isValidationEnabled
        ? Validate.responseType(() => resultPromise, z.unknown(), 'z.unknown()')
        : Validate.unsafeResponse(() => resultPromise)

    if (!this.cache) {
      return SdkCache.withoutCache(res)
    }
    const cacheKey = url + CodeGenUtil.hashCode(JSON.stringify({ params }))
    return SdkCache.withCache(cacheKey, res)
  }

  /**
   * This endpoint is being used to authenticate a user account. It validates user's email and password. Redirect URI and Client ID must be specified as a pair and only used to redirect to the specified redirect URI in case the requestId is no longer valid.
   */
  postOauthAuthenticate(data: {
    username: string | null
    password: string | null
    request_id: string | null
    redirect_uri?: string | null
    client_id?: string | null
  }): Promise<IResponse<unknown>> {
    const params = {} as SDKRequestConfig
    const url = '/ic/v1/oauth/authenticate'
    const resultPromise = this.axiosInstance.post(url, CodeGenUtil.getFormUrlEncodedData(data), {
      ...params,
      headers: { ...params.headers, 'content-type': 'application/x-www-form-urlencoded' }
    })

    return this.isValidationEnabled
      ? Validate.responseType(() => resultPromise, z.unknown(), 'z.unknown()')
      : Validate.unsafeResponse(() => resultPromise)
  }
}
